/*
 * Compliance.tsx — S57: SOX Compliance Dashboard
 * API: GET /api/v1/compliance/dashboard, GET /api/v1/compliance/controls
 */
import { useState, useEffect } from 'react';
import { API_V1, authFetch, safeFetch } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';

interface Control {
  control_id: string;
  control_name: string;
  description: string;
  control_category: string;
  last_test_result: string | null;
  last_tested_at: string | null;
}

const CONTROL_SEVERITY: Record<string, string> = {
  "AA-001": "HIGH", "ACC-001": "HIGH", "AI-001": "MEDIUM",
  "AI-002": "MEDIUM", "DOA-001": "HIGH", "SOD-001": "HIGH",
};

interface ComplianceDash {
  overall_status: string;
  controls_total: number;
  controls_passing: number;
  controls_failing: number;
  controls_not_tested: number;
  last_test_run: string | null;
  findings: Record<string, any>[];
}

export default function Compliance() {
  const [dashboard, setDashboard] = useState<ComplianceDash | null>(null);
  const [controls, setControls] = useState<Control[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState('');

  const load = () => {
    setLoading(true);
    setError('');
    Promise.all([
      safeFetch(`${API_V1}/compliance/dashboard`, null),
      safeFetch(`${API_V1}/compliance/controls`, { controls: [] }),
    ])
      .then(([dash, ctrl]) => {
        setDashboard(dash);
        setControls(Array.isArray(ctrl) ? ctrl : ctrl?.controls || []);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  const downloadExport = async (jobId: number, format: string) => {
    const resp = await authFetch(`${API_V1}/audit/exports/${jobId}/download`);
    const blob = await resp.blob();
    const ext = format === 'both' ? 'zip' : format;
    const today = new Date().toISOString().slice(0, 10);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_package_${today}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExport = async (format: string) => {
    setExporting(format);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const resp = await authFetch(`${API_V1}/audit/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start_date: '2020-01-01', end_date: today, format }),
      });
      const data = await resp.json();

      if (data.job_id) {
        // Poll for completion
        const jobId = data.job_id;
        if (data.status === 'COMPLETED') {
          // Sync mode -- download immediately
          await downloadExport(jobId, format);
        } else {
          // Async -- poll
          const poll = setInterval(async () => {
            try {
              const statusResp = await authFetch(`${API_V1}/audit/exports/${jobId}`);
              const statusData = await statusResp.json();
              if (statusData.status === 'COMPLETED') {
                clearInterval(poll);
                await downloadExport(jobId, format);
                setExporting('');
              } else if (statusData.status === 'FAILED') {
                clearInterval(poll);
                alert('Export failed: ' + (statusData.error_message || 'Unknown error'));
                setExporting('');
              }
            } catch { clearInterval(poll); setExporting(''); }
          }, 3000);
          return; // Don't clear exporting yet
        }
      }
    } catch (e: any) {
      setError(e.message || 'Export failed');
    }
    setExporting('');
  };

  useEffect(() => { load(); }, []);

  const statusColor = (s: string | null) => {
    if (!s) return 'var(--text-hint)';
    if (s === 'PASS' || s === 'COMPLIANT') return 'var(--positive)';
    if (s === 'FAIL' || s === 'NON_COMPLIANT') return 'var(--negative)';
    return 'var(--warning)';
  };

  if (loading) return <PageLayout title="SOX Compliance"><div style={{ padding: 32, color: 'var(--text-hint)' }}>Loading compliance dashboard...</div></PageLayout>;
  if (error) return (
    <PageLayout title="SOX Compliance">
      <div style={{ color: 'var(--negative)', marginBottom: 8 }}>Error: {error}</div>
      <ActionButton label="Retry" onClick={load} variant="secondary" />
    </PageLayout>
  );

  const overallColor = statusColor(dashboard?.overall_status || null);
  const total = dashboard?.controls_total ?? controls.length;
  const passing = dashboard?.controls_passing ?? controls.filter(c => c.last_test_result === 'PASS' || c.last_test_result === 'COMPLIANT').length;
  const failing = dashboard?.controls_failing ?? controls.filter(c => c.last_test_result === 'FAIL' || c.last_test_result === 'NON_COMPLIANT').length;
  const notTested = dashboard?.controls_not_tested ?? controls.filter(c => !c.last_test_result).length;

  const controlColumns: Column[] = [
    { key: 'control_id', label: 'Control ID', render: (v: string) => <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{v}</span> },
    { key: 'control_name', label: 'Name' },
    { key: 'control_category', label: 'Category', render: (v: string) => <span style={{ color: 'var(--text-secondary)' }}>{(v ?? '').replace(/_/g, ' ')}</span> },
    { key: 'severity', label: 'Severity', align: 'center', render: (_: any, row: Control) => {
      const sev = CONTROL_SEVERITY[row.control_id] || 'MEDIUM';
      return (
        <span style={{
          padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
          background: sev === 'HIGH' ? 'rgba(239,68,68,0.08)' : sev === 'MEDIUM' ? 'rgba(245,158,11,0.08)' : 'rgba(34,197,94,0.08)',
          color: sev === 'HIGH' ? 'var(--negative)' : sev === 'MEDIUM' ? 'var(--warning)' : 'var(--positive)',
        }}>
          {sev}
        </span>
      );
    }},
    { key: 'last_test_result', label: 'Status', align: 'center', render: (v: string | null) => (
      <span style={{
        padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
        background: `${statusColor(v)}15`,
        color: statusColor(v),
      }}>
        {v || 'NOT TESTED'}
      </span>
    )},
    { key: 'last_tested_at', label: 'Last Tested', align: 'right', render: (v: string | null) => (
      <span style={{ fontSize: 11, color: 'var(--text-hint)' }}>
        {v ? new Date(v).toLocaleDateString() : '-'}
      </span>
    )},
  ];

  return (
    <PageLayout title="SOX Compliance" subtitle={dashboard?.overall_status ? `Overall: ${dashboard.overall_status}` : undefined}>
      {/* Overall status badge */}
      {dashboard?.overall_status && (
        <div style={{ marginTop: -8, marginBottom: 16 }}>
          <span style={{
            padding: '4px 16px', borderRadius: 4, fontSize: 13, fontWeight: 700,
            background: `${overallColor}15`, color: overallColor,
          }}>
            {dashboard.overall_status}
          </span>
        </div>
      )}

      {(dashboard || controls.length > 0) && (
        <KpiGrid columns={4} items={[
          { label: 'Total Controls', value: total, color: 'var(--text-primary)' },
          { label: 'Passing', value: passing, color: 'var(--positive)' },
          { label: 'Failing', value: failing, color: 'var(--negative)' },
          { label: 'Not Tested', value: notTested, color: 'var(--warning)' },
        ]} />
      )}

      <div style={{ marginBottom: 24 }}>
        <DataTable
          columns={controlColumns}
          rows={controls}
          rowKey="control_id"
          emptyMessage="No controls configured."
        />
      </div>

      {/* Audit Export Section */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: 20, marginBottom: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: 'var(--navy-900)' }}>Audit Export</h3>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
          Generate professional audit evidence packages for external auditors.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <ActionButton
            label={exporting === 'xlsx' ? 'Generating...' : 'Export XLSX Workbook'}
            onClick={() => handleExport('xlsx')}
            disabled={!!exporting}
            variant="secondary"
          />
          <ActionButton
            label={exporting === 'pdf' ? 'Generating...' : 'Export PDF Report'}
            onClick={() => handleExport('pdf')}
            disabled={!!exporting}
            variant="secondary"
          />
          <ActionButton
            label={exporting === 'both' ? 'Generating...' : 'Export Both (ZIP)'}
            onClick={() => handleExport('both')}
            disabled={!!exporting}
            variant="secondary"
          />
        </div>
      </div>

      {dashboard?.last_test_run && (
        <p style={{ fontSize: 12, color: 'var(--text-hint)' }}>
          Last test run: {new Date(dashboard.last_test_run).toLocaleString()}
        </p>
      )}
    </PageLayout>
  );
}
